import type { SortableColumn } from '../types/claims.types';

export interface SortableColumnDef {
  readonly key: SortableColumn;
  readonly label: string;
  readonly align?: 'right';
}

/** Reason has nothing to sort by — it is carried by claim entries, not by the claim header. */
export interface PlainColumnDef {
  readonly key: 'reason';
  readonly label: string;
}

export type Column = SortableColumnDef | PlainColumnDef;

export function isSortable(column: Column): column is SortableColumnDef {
  return column.key !== 'reason';
}

/**
 * The column set US-009 AC-003 and US-015 AC-001 fix, in that order.
 *
 * This list is the only place the order is stated. Reason used to be appended by the grid and the row
 * separately, which is how it came to sit at the end of the table instead of where it belongs (PP-025):
 * a column whose position lives in two components has no single position at all.
 */
export const COLUMNS: readonly Column[] = [
  { key: 'claimNumber', label: 'Claim #' },
  { key: 'npi', label: 'NPI' },
  { key: 'icn', label: 'ICN' },
  // PP-023. The field behind it is still `location`; the deployed API returns the same string in its
  // own `providerName`, so the acceptance criteria's name describes what the column already shows.
  { key: 'location', label: 'Provider Name' },
  { key: 'procCode', label: 'Proc Code' },
  { key: 'svcCode', label: 'Svc Code' },
  { key: 'serviceDateFrom', label: 'Date of Service' },
  { key: 'paymentDate', label: 'Payment Date' },
  { key: 'status', label: 'Status' },
  // PP-025: Reason reads as part of the status, so it follows it rather than trailing the amounts.
  { key: 'reason', label: 'Reason' },
  { key: 'billedAmount', label: 'Billed Amt', align: 'right' },
  { key: 'medicareAllowedAmount', label: 'Medicare Allowed Amt', align: 'right' },
  { key: 'paidAmount', label: 'Medicare Paid Amt', align: 'right' },
  // PP-014. Beside the other money columns rather than at the end of the row: a total the provider
  // reads against Medicare Paid is no use to them a column away from it.
  { key: 'carrierPaidAmount', label: 'Carrier Paid Amt', align: 'right' },
  { key: 'checkNumber', label: 'Check #' },
];

